import { useState, useEffect } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ViewMode } from "@/types/contractor";
import { useToast } from "@/hooks/use-toast";
import { useSearchParams, useLocation } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { SearchSection } from "@/components/index/SearchSection";
import { FeaturedSection } from "@/components/index/FeaturedSection";
import { ContractorsSection } from "@/components/index/ContractorsSection";
import { FAQSection } from "@/components/index/FAQSection";
import { useContractors } from "@/components/index/useContractors";
import { FilteredContractors } from "@/components/index/FilteredContractors";
import { generateStructuredData } from "@/components/index/StructuredData";
import { getTopRatedContractors } from "@/components/index/TopRatedContractors";
import { ExitIntentPopup } from "@/components/ExitIntentPopup";
import { trackContractorSearch } from "@/utils/metaPixel";
// Import the console utility for duplicate checking
import "@/utils/consoleUtils";

const FallbackUI = () => (
  <div className="text-center py-16 px-4">
    <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Homepage</h2>
    <p className="mb-4">We're experiencing technical difficulties. Please try refreshing the page.</p>
    <button
      onClick={() => window.location.reload()}
      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
    >
      Refresh Page
    </button>
  </div>
);

const Index = () => {
  console.log('Index component rendering');
  const [searchParams] = useSearchParams();
  const routerLocation = useLocation();
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [serviceType, setServiceType] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const { toast } = useToast();

  useEffect(() => {
    console.log('Index component mounted');
    return () => {
      console.log('Index component unmounted');
    };
  }, []);

  // Reset filters when navigating from header
  useEffect(() => {
    console.log('Location state changed:', routerLocation.state);
    if (routerLocation.state?.resetFilters) {
      setSearchQuery("");
      setServiceType("all");
      setLocationFilter("all");
      window.history.replaceState({}, document.title);
    }
  }, [routerLocation]);

  // Get the service type from URL parameters
  useEffect(() => {
    console.log('Search params changed:', searchParams.get('service'));
    const serviceFromUrl = searchParams.get('service');
    if (serviceFromUrl) {
      const formattedService = serviceFromUrl
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      setServiceType(formattedService.toLowerCase());
      
      toast({
        title: "Filter Applied",
        description: `Showing ${formattedService} professionals`,
      });
    }
  }, [searchParams, toast]);

  const { data: contractors = [], isLoading, error } = useContractors(serviceType, locationFilter);

  // Log any errors from the contractors query
  useEffect(() => {
    if (error) {
      console.error('Error fetching contractors:', error);
      toast({
        title: "Error",
        description: "Failed to load contractors. Please try again.",
        variant: "destructive",
      });
    }
  }, [error, toast]);

  const handleSearch = (query: string) => {
    console.log('Search query:', query);
    setSearchQuery(query);
    
    // Track search with Meta Pixel
    if (query.trim()) {
      const filteredResults = FilteredContractors({ 
        contractors: contractors, 
        searchQuery: query 
      });
      trackContractorSearch(query, filteredResults.length);
    }
  };

  const handleServiceTypeChange = (type: string) => {
    console.log('Service type changed to:', type);
    setServiceType(type);
    toast({
      title: "Filter Applied",
      description: `Showing ${type === "all" ? "all services" : type} contractors`,
    });
  };

  const handleLocationChange = (loc: string) => {
    console.log('Location changed to:', loc);
    setLocationFilter(loc);
    toast({
      title: "Location Filter Applied",
      description: `Showing contractors in ${loc === "all" ? "all locations" : loc.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}`,
    });
  };

  try {
    // Create a fallback array if contractors is undefined
    const contractorsArray = Array.isArray(contractors) ? contractors : [];
    
    const filteredContractors = FilteredContractors({ 
      contractors: contractorsArray, 
      searchQuery 
    });

    // Get top rated featured contractors for featured section
    const topRatedContractors = getTopRatedContractors(contractorsArray);

    // Show featured section only when there's no search query and no filters
    const showFeatured = searchQuery === "" && serviceType === "all" && locationFilter === "all";

    console.log('Rendering Index with:', {
      searchQuery,
      serviceType,
      locationFilter,
      totalContractors: contractorsArray.length,
      filteredCount: filteredContractors.length,
      showFeatured,
      featuredCount: topRatedContractors.length,
      isLoading,
      hasError: !!error
    });

    return (
      <ErrorBoundary fallback={<FallbackUI />}>
        <SEO 
          title="Find Trusted Home Service Professionals | Home Pros OKC"
          description="Connect with established home service professionals in Oklahoma City. Find reliable contractors, read reviews, and get quotes for your home improvement projects."
          canonicalUrl="/"
          schema={generateStructuredData(contractorsArray)}
        />
        <div className="flex flex-col min-h-screen">
          <SearchSection onSearch={handleSearch} />
          <FeaturedSection 
            contractors={topRatedContractors} 
            showFeatured={showFeatured}
          />
          <ContractorsSection
            contractors={filteredContractors}
            isLoading={isLoading}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onServiceTypeChange={handleServiceTypeChange}
            onLocationChange={handleLocationChange}
            selectedServiceType={serviceType}
          />
          <FAQSection />
          <ExitIntentPopup />
        </div>
      </ErrorBoundary>
    );
  } catch (err) {
    console.error('Error in Index component render:', err);
    return <FallbackUI />;
  }
};

export default Index;
